import OpenInNew from '@mui/icons-material/OpenInNew';
import { useAuthenticatedUser } from '@src/authentication/useAuthenticatedUser';
import { IconButton } from '@src/common/components/IconButton';
import { Menu } from '@src/common/components/Menu';
import { Stack } from '@src/common/components/Stack';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { initializeExtension } from '@src/extensionBridge/InitializeExtension';
import DevFlags, { DevFlag } from '@src/helpers/DevFlags';
import { AmendPermit } from '@src/orders/components/permitList/PermitActions/AmendPermit';
import { CancelPermit } from '@src/orders/components/permitList/PermitActions/CancelPermit';
import { CompleteApplication } from '@src/orders/components/permitList/PermitActions/CompleteApplication';
import { CompleteForm } from '@src/orders/components/permitList/PermitActions/CompleteForm';
import { KeepAsIs } from '@src/orders/components/permitList/PermitActions/KeepAsIs';
import { ModifyStateApplication } from '@src/orders/components/permitList/PermitActions/ModifyStateApplication';
import { ModifyWithJurisdiction } from '@src/orders/components/permitList/PermitActions/ModifyWithJurisdiction';
import { ProcessWithJurisdiction } from '@src/orders/components/permitList/PermitActions/ProcessWithJurisdiction';
import { ReopenForCorrection } from '@src/orders/components/permitList/PermitActions/ReopenForCorrection';
import { ReorderPermit } from '@src/orders/components/permitList/PermitActions/ReorderPermit';
import { ReplacePermitType } from '@src/orders/components/permitList/PermitActions/ReplacePermitType';
import { ViewApplication } from '@src/orders/components/permitList/PermitActions/ViewApplication';
import type { OrderListing } from '@src/orders/domain/OrderList';
import type { PermitSummary } from '@src/orders/domain/PermitSummary';
import { PermitNotesButton } from '@src/orders/review/PermitNotesButton';
import { PermitActionRulesService } from '@src/orders/services/PermitActionRulesService';
import { DotsVertical } from '@untitled-ui/icons-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ActionsCellProps {
  order: OrderListing;
  permit: PermitSummary;
}

export const PermitActions = ({ order, permit }: ActionsCellProps) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuthenticatedUser();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const userCantProcessWithJurisdiction = !order.isBillable();

  const openWithExtension = () => {
    initializeExtension({
      permit,
      language: i18n.language,
      userPermissions: user?.permissions,
    });
    openJurisdiction();
  };

  const openJurisdiction = () => {
    window.open(permit.jurisdictionUrl, '_blank');
  };

  const permitManagerAutofillIsEnabled = DevFlags.isEnabled(
    DevFlag.PERMIT_MANAGER_AUTOFILL_EXTENSION,
  );

  const rules = PermitActionRulesService.computeActionRules(permit, order);

  const getMenuOptions = () => {
    const menuOptions = [];
    if (rules.canCompleteApplication) {
      menuOptions.push(
        <CompleteApplication
          key="complete-application"
          permitId={permit.id}
          orderId={order.id}
        />,
      );
    }

    if (rules.canView) {
      menuOptions.push(
        <ViewApplication
          key="view-application"
          permitId={permit.id}
          orderId={order.id}
        />,
      );
    }

    if (rules.shouldCompleteForm) {
      menuOptions.push(
        <CompleteForm
          key="complete-form"
          orderId={order.id}
          permitId={permit.id}
        />,
      );
    }

    if (rules.canProcessWithJurisdiction) {
      menuOptions.push(
        <ProcessWithJurisdiction
          key="process-with-jurisdiction"
          permitId={permit.id}
          orderId={order.id}
          disabled={userCantProcessWithJurisdiction}
        />,
      );
    }

    if (rules.canModifyStateApplication) {
      menuOptions.push(
        <ModifyStateApplication
          key="modify-state-application"
          permitId={permit.id}
          orderId={order.id}
          disabled={userCantProcessWithJurisdiction}
        />,
      );
    }

    if (rules.canReorder) {
      menuOptions.push(
        <ReorderPermit
          key="reorder-permit"
          permitId={permit.id}
          orderId={order.id}
        />,
      );
    }

    if (rules.canAmend) {
      menuOptions.push(
        <AmendPermit
          key="amend-permit"
          permitId={permit.id}
          orderId={order.id}
        />,
      );
    }
    if (rules.canModifyWithJurisdiction) {
      menuOptions.push(
        <ModifyWithJurisdiction
          key="modify-with-jurisdiction"
          permitId={permit.id}
          orderId={order.id}
        />,
      );
    }

    if (rules.canKeepAsIs) {
      menuOptions.push(
        <KeepAsIs key="keep-as-is" permitId={permit.id} orderId={order.id} />,
      );
    }

    if (rules.canReplaceType) {
      menuOptions.push(
        <ReplacePermitType
          key="replace-permit-type"
          permit={permit}
          orderId={order.id}
        />,
      );
    }

    if (rules.canCancel) {
      menuOptions.push(
        <CancelPermit key="cancel-permit" permit={permit} order={order} />,
      );
    }

    if (rules.canReopenForCorrection) {
      menuOptions.push(
        <ReopenForCorrection
          key="reopen-permit"
          permit={permit}
          order={order}
        />,
      );
    }

    return menuOptions;
  };

  const menuOptions = getMenuOptions();
  const actionsButtonStyle = open
    ? {
        backgroundColor: theme.palette.primary.primary,
        color: theme.palette.primary.onPrimary,
      }
    : {};

  return (
    <Stack direction="row" justifyContent="flex-end">
      <PermitNotesButton permitId={permit.id} mode="permitNotes" />
      {rules.canProcessWithJurisdiction && permit.jurisdictionUrl && (
        <IconButton
          onClick={
            permitManagerAutofillIsEnabled
              ? openWithExtension
              : openJurisdiction
          }
          title={t('openJurisdictionForm')}
          disabled={userCantProcessWithJurisdiction}
        >
          <OpenInNew />
        </IconButton>
      )}
      {rules.canModifyStateApplication && permit.jurisdictionUrl && (
        <IconButton
          onClick={() => window.open(permit.jurisdictionUrl, '_blank')}
          title={t('openJurisdictionForm')}
        >
          <OpenInNew />
        </IconButton>
      )}
      {menuOptions.length > 0 && (
        <span>
          <IconButton
            style={actionsButtonStyle}
            aria-label={t('permitActions')}
            onClick={(event) => handleMenuClick(event)}
          >
            <DotsVertical />
          </IconButton>
          <Menu
            className={menuStyle}
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
          >
            {menuOptions}
          </Menu>
        </span>
      )}
    </Stack>
  );
};

const menuStyle = css((theme) => ({
  marginTop: theme.spacing['1'],
  '& .MuiPaper-root': {
    backgroundColor: theme.palette.surface.surfaceContainerHigh,
  },
}));
